<template>
    <input
        type="text"
        @mousedown="togglePicker"
        @mouseleave="togglePicker"
        @mouseup="togglePicker"
        @touchstart="togglePicker"
    >
</template>

<script>
    import 'pickadate/lib/picker.js';
    import 'pickadate/lib/picker.time.js';
    import cloneDeep from 'lodash/cloneDeep';
    import extend from 'lodash/extend';

    export default {
        name: 'timepicker',

        props: ['options', 'value'],

        data() {
            return {
                pickerId: '',
                isTouch: false,
                isSafari: false,
            };
        },

        watch: {
            value() {
                this.createTimepicker();
            },

            options() {
                this.createTimepicker();
            },
        },

        created() {
            this.instance = null;
            const $vm = this;

            this.onRender = function() {
                $('.picker__list-item', this.$holder).each((i, time) => {
                    const $time = $(time);
                    const timeString = $time.attr('aria-label');

                    if (timeString === '23:45') {
                        $time.text('23:59');
                    }

                    if (!['00', '30'].includes(timeString.substr(-2)) && timeString !== '23:45') {
                        $time.hide();
                    }
                });
            };

            this.onSet = function() {
                const val = $vm.$el.value;

                $vm.$emit('input', val);

                if (val === '23:45') {
                    $vm.$el.value = '23:59';
                }
            };

            this.onClose = function() {
                if (!$vm.isSafari) {
                    $vm.$el.blur();

                    if (!$vm.isTouch) {
                        $vm.$el.style.pointerEvents = 'auto';
                    }
                }
            };

            this.onOpen = function() {
                if (!$vm.isSafari) {
                    $vm.$el.focus();

                    if (!$vm.isTouch) {
                        $vm.$el.style.pointerEvents = 'none';
                    }
                }
            };
        },

        mounted() {
            this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
            this.createTimepicker();

            bus.$on('openTimePicker', (obj) => {
                if (this.$el.getAttribute('id') === obj.id) {
                    this.instance.open(false);
                }
            });
        },

        methods: {
            /**
             * Create the timepicker instance
             */
            createTimepicker() {
                const options = extend(cloneDeep(this.options), {
                    clear: false,
                    format: 'HH:i',
                    onRender: this.onRender,
                    onSet: this.onSet,
                    onClose: this.onClose,
                    onOpen: this.onOpen,
                });

                $(this.$el)
                    .val(this.value)
                    .pickatime(options);

                // For some reason this option breaks it
                delete options.clear;

                // Use the picker api to change options
                this.instance = $(this.$el).pickatime('picker');
                this.instance.set(options);

                //Resets disabled dates when dates are updated on booking form
                this.instance.set('disable', false);

                if (options.disable) {
                    this.instance.set('disable', options.disable);
                }
            },

            /**
             * Returns root element of the timepicker widget if timepicker is opened.
             * @returns {HTMLElement|null}
             */
            getPickerContainerEl() {
                return this.instance ? this.instance.$root[0] : null;
            },

            /**
             * Toggle the picker.
             *
             * @param {HTMLElement} event
             */
            togglePicker(event) {
                const hasPickerOpenedClass = document.querySelector('.picker--opened');

                if (event.type === 'touchstart') {
                    this.isTouch = true;
                }

                if (this.isTouch) {
                    if (hasPickerOpenedClass) {
                        this.pickerId = hasPickerOpenedClass.getAttribute('id').split('_')[0];
                        if (event.target.id === this.pickerId) {
                            setTimeout(() => {
                                this.instance.close();
                                this.pickerId = '';
                            }, this.isSafari ? 500 : 100);
                        }
                    }

                    return;
                }

                if (event.type === 'mousedown') {
                    if (hasPickerOpenedClass) {
                        this.pickerId = hasPickerOpenedClass.getAttribute('id').split('_')[0];
                        this.$el.style.pointerEvents = 'none';
                        if (event.target.id === this.pickerId) {
                            setTimeout(() => {
                                this.instance.close();
                                this.pickerId = '';
                            }, 100);
                        }
                    }
                } else {
                    this.$el.style.pointerEvents = 'auto';
                }
            },
        },
    };
</script>

<style lang="less">
    @import (reference) "~assets/less/variables.less";
    @import (less) "~pickadate/lib/themes/classic.css";
    @import (less) "~pickadate/lib/themes/classic.time.css";
    @import "~bootstrap/less/mixins/responsive-visibility";
    @import (reference) "~bootstrap/less/mixins.less";
    @import (reference) "~bootstrap/less/scaffolding.less";

    .form-control.picker__input {
        cursor: pointer;
        user-select: none;

        &.picker__input--active {
            position: relative;
            z-index: 5;
        }
    }
    .picker {
        top: 100%;
        z-index: 4;

        &__holder {
            overflow: initial;
        }

        &__frame {
            max-height: 25em;
            overflow-y: auto;
        }
    }
    .picker__input {
        transform: translate3d(0, 0, 0);
        cursor: pointer;

        &:not(.picker__input--active) + .picker--time {
            .sr-only;
        }

        &[disabled] {
            cursor: not-allowed !important; // important due to assets/less/components/forms.less line 20
        }
    }
    .picker--time {
        min-width: auto;
        .picker__holder {
            min-width: auto;
            outline: 0;
            box-shadow: 1px 0 0 #e6e6e6, -1px 0 0 #e6e6e6, 0 1px 0 #e6e6e6, 0 -1px 0 #e6e6e6, 0 3px 13px rgba(0,0,0,0.08);
            top: 100%;
            border: 1px solid #c9d6e3;
            border-radius: 0 0 2px 2px;
        }
        .picker__list {
            padding: 0;
        }
        .picker__list-item {
            background-color: #ffffff;
            color: #4d555f;
            font-family: @font-family-base;
            font-size: 13px;
            font-weight: 400;
            padding: 10px 14px;
            appearance: none;
            &:hover,
            &.picker__list-item--highlighted {
                background: @YPS-secondary;
                border-color: @YPS-secondary;
                color: white;
            }
            &.picker__list-item--disabled {
                background-color: rgba(128,128,128,0.25);
                cursor: not-allowed;
                border-color: #f5f5f5;
            }
        }
    }
</style>

